import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  cancelAppointmentOrder,
  getAppointmentOrders,
  resetAppointmentOrder
} from '../api/reportApi';
import { getAppointmentOrderTypeLabel } from '../utils/appointmentOrderOptions';
import { formatDateTime, formatPatientName, normalizeUiLocale } from '../utils/locale';

const AppointmentOrdersQueue = () => {
  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [sourceClinicFilter, setSourceClinicFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const data = await getAppointmentOrders(params);
      setOrders(data || []);
      setSelectedOrderId((current) => {
        const nextOrders = data || [];
        if (!nextOrders.length) {
          return null;
        }
        if (current && nextOrders.some((order) => order.id === current)) {
          return current;
        }
        return nextOrders[0].id;
      });
    } catch (requestError) {
      console.error('获取预约单队列失败:', requestError);
      setError(t('appointmentOrders.loadError', {
        message: requestError.response?.data?.error || requestError.message || t('common.unknownError')
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedStatus = params.get('status');
    const requestedOrderId = Number(params.get('order_id'));
    const validStatuses = ['pending', 'claimed', 'completed', 'cancelled', 'all'];

    if (requestedStatus && validStatuses.includes(requestedStatus)) {
      setStatusFilter(requestedStatus);
      return;
    }

    if (requestedOrderId > 0) {
      setStatusFilter('all');
    }
  }, [location.search]);

  const clinicOptions = useMemo(() => {
    const clinicMap = new Map();
    orders.forEach((order) => {
      const clinicId = order.source_clinic_id || 'unknown';
      const clinicLabel = order.source_clinic_name || (order.source_clinic_id
        ? t('appointmentOrders.clinicNumber', { id: order.source_clinic_id })
        : t('appointmentOrders.unknownClinic'));
      clinicMap.set(String(clinicId), clinicLabel);
    });
    return Array.from(clinicMap.entries()).map(([value, label]) => ({ value, label }));
  }, [orders, t]);

  const filteredOrders = useMemo(() => {
    if (sourceClinicFilter === 'all') {
      return orders;
    }
    if (sourceClinicFilter === 'unknown') {
      return orders.filter((order) => !order.source_clinic_id);
    }
    return orders.filter((order) => String(order.source_clinic_id) === sourceClinicFilter);
  }, [orders, sourceClinicFilter]);

  const selectedOrder = filteredOrders.find((order) => order.id === selectedOrderId) || filteredOrders[0] || null;
  const getOrderTitle = (order) => (
    order?.description || getAppointmentOrderTypeLabel(order?.check_type, t)
  );
  const getOrderPatientName = (order) => formatPatientName(
    order,
    locale,
    order?.patient_name || t('appointmentOrders.unnamedPatient')
  );

  useEffect(() => {
    if (!selectedOrder && filteredOrders.length > 0) {
      setSelectedOrderId(filteredOrders[0].id);
    }
  }, [filteredOrders, selectedOrder]);

  useEffect(() => {
    const requestedOrderID = Number(new URLSearchParams(location.search).get('order_id'));
    if (requestedOrderID > 0 && filteredOrders.some((order) => order.id === requestedOrderID)) {
      setSelectedOrderId(requestedOrderID);
    }
  }, [filteredOrders, location.search]);

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return t('appointmentOrders.notRecorded');
    }
    return formatDateTime(dateValue, locale) || t('appointmentOrders.notRecorded');
  };

  const formatClinic = (name, id) => {
    if (name) {
      return name;
    }
    if (id) {
      return t('appointmentOrders.clinicNumber', { id });
    }
    return t('appointmentOrders.unknownClinic');
  };

  const formatStatus = (status) => t(`appointmentOrders.${status}`, { defaultValue: status });

  const getStatusClass = (status) => {
    if (status === 'claimed') return 'status-pill--cool';
    if (status === 'completed') return 'status-pill--success';
    if (status === 'cancelled') return 'status-pill--danger';
    return 'status-pill--warning';
  };

  const statusCounts = useMemo(() => {
    return orders.reduce((accumulator, order) => {
      accumulator[order.status] = (accumulator[order.status] || 0) + 1;
      return accumulator;
    }, {});
  }, [orders]);

  const getClaimVisitLink = (order) => {
    if (!order?.claim_visit_id) {
      return null;
    }

    return order.status === 'claimed'
      ? `/reports?visit_id=${order.claim_visit_id}`
      : `/reviewed-reports?visit_id=${order.claim_visit_id}`;
  };

  const handleOrderAction = async (action) => {
    if (!selectedOrder) {
      return;
    }

    const confirmMessage = action === 'cancel'
      ? t('appointmentOrders.cancelConfirm')
      : t('appointmentOrders.resetConfirm');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setActing(true);
      setError('');
      setSuccessMessage('');

      if (action === 'cancel') {
        await cancelAppointmentOrder(selectedOrder.id);
      } else {
        await resetAppointmentOrder(selectedOrder.id);
      }

      setSuccessMessage(
        action === 'cancel'
          ? t('appointmentOrders.cancelSuccess')
          : t('appointmentOrders.resetSuccess')
      );

      await fetchOrders();
    } catch (requestError) {
      console.error('预约单操作失败:', requestError);
      setError(t('appointmentOrders.updateError', {
        message: requestError.response?.data?.error || requestError.message || t('common.unknownError')
      }));
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">{t('appointmentOrders.listTitle')}</h1>
        </div>

        <div className="page-actions">
          <button type="button" className="theme-button theme-button--secondary" onClick={fetchOrders}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="status-banner status-banner--success">
          <span>{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      <section className="surface-panel">
        <div className="surface-panel__body stack">
          <div className="toolbar-cluster">
            <span className="status-pill status-pill--warning">{t('appointmentOrders.pendingCount', { count: statusCounts.pending || 0 })}</span>
            <span className="status-pill status-pill--cool">{t('appointmentOrders.claimedCount', { count: statusCounts.claimed || 0 })}</span>
            <span className="status-pill status-pill--success">{t('appointmentOrders.completedCount', { count: statusCounts.completed || 0 })}</span>
          </div>

          <div className="filter-row">
            <div className="field">
              <label className="field__label" htmlFor="appointment-status-filter">
                {t('appointmentOrders.status')}
              </label>
              <select
                id="appointment-status-filter"
                className="theme-select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="pending">{t('appointmentOrders.pending')}</option>
                <option value="claimed">{t('appointmentOrders.claimed')}</option>
                <option value="completed">{t('appointmentOrders.completed')}</option>
                <option value="cancelled">{t('appointmentOrders.cancelled')}</option>
                <option value="all">{t('appointmentOrders.allStatuses')}</option>
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="appointment-clinic-filter">
                {t('appointmentOrders.sourceClinic')}
              </label>
              <select
                id="appointment-clinic-filter"
                className="theme-select"
                value={sourceClinicFilter}
                onChange={(event) => setSourceClinicFilter(event.target.value)}
              >
                <option value="all">{t('appointmentOrders.allClinics')}</option>
                {clinicOptions.map((clinic) => (
                  <option key={clinic.value} value={clinic.value}>
                    {clinic.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      <div className="appointment-queue-grid">
        <section className="surface-panel">
          <div className="surface-panel__body stack">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-primary)' }}>
                  {t('appointmentOrders.queueTitle')}
                </h2>
                <p style={{ margin: '0.35rem 0 0', color: 'var(--text-secondary)' }}>
                  {t('appointmentOrders.shownCount', { count: filteredOrders.length })}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-orb" />
                <span>{t('appointmentOrders.loading')}</span>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="empty-state">
                <h3 className="auth-form__title">{t('appointmentOrders.emptyTitle')}</h3>
                <p className="auth-form__subtitle">{t('appointmentOrders.emptyHint')}</p>
              </div>
            ) : (
              <div style={{ display: 'grid' }}>
                {filteredOrders.map((order, index) => (
                  <button
                    key={order.id}
                    type="button"
                    className={`appointment-queue-row ${selectedOrder?.id === order.id ? 'is-selected' : ''}`}
                    style={{ animationDelay: `${index * 28}ms` }}
                    onClick={() => setSelectedOrderId(order.id)}
                  >
                    <div style={{ display: 'grid', gap: '0.45rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '0.55rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span className={`status-pill ${getStatusClass(order.status)}`}>{formatStatus(order.status)}</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {getOrderTitle(order)}
                          </span>
                        </div>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                          #{order.id}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', color: 'var(--text-secondary)', fontSize: '0.92rem' }}>
                        <span><bdi dir="auto">{getOrderPatientName(order)}</bdi></span>
                        <span>{formatClinic(order.source_clinic_name, order.source_clinic_id)}</span>
                        <span>{t('appointmentOrders.sourceVisitNumber', { id: order.source_visit_id })}</span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                        <span>
                          {t('appointmentOrders.claimClinicValue', {
                            clinic: formatClinic(order.claim_clinic_name, order.claim_clinic_id)
                          })}
                        </span>
                        <span>{formatDate(order.created_at)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className="surface-panel surface-panel--soft">
          <div className="surface-panel__body stack">
            <div>
              <h2 style={{ margin: '0.45rem 0 0', fontSize: '1.3rem', color: 'var(--text-primary)' }}>
                {selectedOrder
                  ? getOrderTitle(selectedOrder)
                  : t('appointmentOrders.details')}
              </h2>
            </div>

            {!selectedOrder ? (
              <p className="auth-form__subtitle">{t('appointmentOrders.selectHint')}</p>
            ) : (
              <>
                <div className="appointment-inspector-row">
                  <strong style={{ color: 'var(--text-muted)' }}>{t('appointmentOrders.patient')}</strong>
                  <span><bdi dir="auto">{getOrderPatientName(selectedOrder)}</bdi></span>
                </div>
                <div className="appointment-inspector-row">
                  <strong style={{ color: 'var(--text-muted)' }}>{t('appointmentOrders.sourceClinic')}</strong>
                  <span>{formatClinic(selectedOrder.source_clinic_name, selectedOrder.source_clinic_id)}</span>
                </div>
                <div className="appointment-inspector-row">
                  <strong style={{ color: 'var(--text-muted)' }}>{t('appointmentOrders.sourceVisit')}</strong>
                  <span>#{selectedOrder.source_visit_id}</span>
                </div>
                <div className="appointment-inspector-row">
                  <strong style={{ color: 'var(--text-muted)' }}>{t('appointmentOrders.claimClinic')}</strong>
                  <span>{formatClinic(selectedOrder.claim_clinic_name, selectedOrder.claim_clinic_id)}</span>
                </div>
                <div className="appointment-inspector-row">
                  <strong style={{ color: 'var(--text-muted)' }}>{t('appointmentOrders.claimVisit')}</strong>
                  <span>{selectedOrder.claim_visit_id ? `#${selectedOrder.claim_visit_id}` : t('appointmentOrders.notClaimed')}</span>
                </div>
                <div className="appointment-inspector-row">
                  <strong style={{ color: 'var(--text-muted)' }}>{t('appointmentOrders.status')}</strong>
                  <span>{formatStatus(selectedOrder.status)}</span>
                </div>
                <div className="appointment-inspector-row">
                  <strong style={{ color: 'var(--text-muted)' }}>{t('appointmentOrders.createdAt')}</strong>
                  <span>{formatDate(selectedOrder.created_at)}</span>
                </div>
                <div className="appointment-inspector-row">
                  <strong style={{ color: 'var(--text-muted)' }}>{t('appointmentOrders.description')}</strong>
                  <span>{selectedOrder.description || t('appointmentOrders.noDescription')}</span>
                </div>

                <div className="inline-link-row">
                  {selectedOrder.source_report_id && (
                    <Link className="inline-link" to={`/reports/${selectedOrder.source_report_id}/view`}>
                      {t('appointmentOrders.openSourceReport')}
                    </Link>
                  )}
                  <Link className="inline-link" to={`/reviewed-reports?visit_id=${selectedOrder.source_visit_id}`}>
                    {t('appointmentOrders.openSourceVisit')}
                  </Link>
                  {selectedOrder.claim_visit_id && (
                    <Link className="inline-link" to={getClaimVisitLink(selectedOrder)}>
                      {selectedOrder.status === 'claimed'
                        ? t('appointmentOrders.openClaimQueue')
                        : t('appointmentOrders.openClaimHistory')}
                    </Link>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {selectedOrder.status === 'pending' && (
                    <button
                      type="button"
                      className="theme-button theme-button--danger"
                      disabled={acting}
                      onClick={() => handleOrderAction('cancel')}
                    >
                      {t('appointmentOrders.cancel')}
                    </button>
                  )}

                  {selectedOrder.status === 'claimed' && (
                    <button
                      type="button"
                      className="theme-button theme-button--secondary"
                      disabled={acting}
                      onClick={() => handleOrderAction('reset')}
                    >
                      {t('appointmentOrders.reset')}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default AppointmentOrdersQueue;
